import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  useCreateOrganization,
  useDeleteOrganization,
  useOrganizations,
  useUpdateOrganization,
} from '@/hooks/use-admin'
import type { OrganizationWithUserCount } from '@/services/admin.service'
import { ChevronLeft, ChevronRight, Pencil, Plus, Search, Trash2, Users } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

interface OrganizationFormData {
  name: string
  slug: string
  description: string
  layoff_date: string
  website: string
  hr_contact_email: string
  hr_contact_name: string
}

const emptyFormData: OrganizationFormData = {
  name: '',
  slug: '',
  description: '',
  layoff_date: '',
  website: '',
  hr_contact_email: '',
  hr_contact_name: '',
}

export function AdminOrganizationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingOrg, setEditingOrg] = useState<OrganizationWithUserCount | null>(null)
  const [formData, setFormData] = useState<OrganizationFormData>(emptyFormData)

  const page = Number(searchParams.get('page')) || 1
  const search = searchParams.get('search') || ''

  const { data, isLoading } = useOrganizations({ page, limit: 20, ...(search && { search }) })
  const createOrganization = useCreateOrganization()
  const updateOrganization = useUpdateOrganization()
  const deleteOrganization = useDeleteOrganization()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams(searchParams)
    if (searchInput) {
      params.set('search', searchInput)
    } else {
      params.delete('search')
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  const handleDelete = async (org: OrganizationWithUserCount) => {
    if (
      confirm(
        `Are you sure you want to delete ${org.name}? Users in this organization will no longer be associated with it.`,
      )
    ) {
      await deleteOrganization.mutateAsync(org.id)
    }
  }

  const handleOpenCreate = () => {
    setFormData(emptyFormData)
    setIsCreateOpen(true)
  }

  const handleOpenEdit = (org: OrganizationWithUserCount) => {
    setFormData({
      name: org.name,
      slug: org.slug,
      description: org.description || '',
      layoff_date: org.layoff_date || '',
      website: org.website || '',
      hr_contact_email: org.hr_contact_email || '',
      hr_contact_name: org.hr_contact_name || '',
    })
    setEditingOrg(org)
  }

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    await createOrganization.mutateAsync({
      name: formData.name,
      ...(formData.slug && { slug: formData.slug }),
      ...(formData.description && { description: formData.description }),
      ...(formData.layoff_date && { layoff_date: formData.layoff_date }),
      ...(formData.website && { website: formData.website }),
      ...(formData.hr_contact_email && { hr_contact_email: formData.hr_contact_email }),
      ...(formData.hr_contact_name && { hr_contact_name: formData.hr_contact_name }),
    })
    setIsCreateOpen(false)
    setFormData(emptyFormData)
  }

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingOrg) return

    await updateOrganization.mutateAsync({
      id: editingOrg.id,
      data: {
        name: formData.name,
        ...(formData.slug && { slug: formData.slug }),
        ...(formData.description && { description: formData.description }),
        ...(formData.layoff_date && { layoff_date: formData.layoff_date }),
        ...(formData.website && { website: formData.website }),
        ...(formData.hr_contact_email && { hr_contact_email: formData.hr_contact_email }),
        ...(formData.hr_contact_name && { hr_contact_name: formData.hr_contact_name }),
      },
    })
    setEditingOrg(null)
    setFormData(emptyFormData)
  }

  const formFields = (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Google"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            placeholder="google (auto-generated if empty)"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of the organization"
          rows={3}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="layoff_date">Layoff Date</Label>
          <Input
            id="layoff_date"
            type="date"
            value={formData.layoff_date}
            onChange={(e) => setFormData({ ...formData, layoff_date: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            type="url"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            placeholder="https://example.com"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="hr_contact_name">HR Contact Name</Label>
          <Input
            id="hr_contact_name"
            value={formData.hr_contact_name}
            onChange={(e) => setFormData({ ...formData, hr_contact_name: e.target.value })}
            placeholder="Jane Smith"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="hr_contact_email">HR Contact Email</Label>
          <Input
            id="hr_contact_email"
            type="email"
            value={formData.hr_contact_email}
            onChange={(e) => setFormData({ ...formData, hr_contact_email: e.target.value })}
            placeholder="hr@example.com"
          />
        </div>
      </div>
    </>
  )

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Organizations</h1>
          <p className="text-muted-foreground">Manage user organizations (companies)</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleOpenCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Organization
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Organization</DialogTitle>
              <DialogDescription>
                Add a new organization to group users (e.g., by company layoff cohort).
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitCreate} className="space-y-4">
              {formFields}
              <DialogFooter>
                <Button type="submit" disabled={createOrganization.isPending}>
                  Create Organization
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Organizations</CardTitle>
              <CardDescription>{data?.total ?? 0} organizations total</CardDescription>
            </div>
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search organizations..."
                  className="pl-8 w-64"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <Button type="submit" variant="secondary">
                Search
              </Button>
            </form>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="h-10 px-4 text-left text-sm font-medium">Name</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Slug</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Users</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Layoff Date</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Status</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Created</th>
                      <th className="h-10 px-4 text-right text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.organizations.map((org) => (
                      <tr key={org.id} className="border-b">
                        <td className="p-4">
                          <div className="font-medium">{org.name}</div>
                          {org.description && (
                            <div className="text-xs text-muted-foreground truncate max-w-xs">
                              {org.description}
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{org.slug}</td>
                        <td className="p-4">
                          <Link
                            to={`/users?organization_id=${org.id}`}
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                          >
                            <Users className="h-3 w-3" />
                            {org.user_count}
                          </Link>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {org.layoff_date ? new Date(org.layoff_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-4">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                              org.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {org.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {new Date(org.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Dialog
                              open={editingOrg?.id === org.id}
                              onOpenChange={(open: boolean) => !open && setEditingOrg(null)}
                            >
                              <DialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenEdit(org)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-lg">
                                <DialogHeader>
                                  <DialogTitle>Edit Organization</DialogTitle>
                                  <DialogDescription>
                                    Update the organization details.
                                  </DialogDescription>
                                </DialogHeader>
                                <form onSubmit={handleSubmitEdit} className="space-y-4">
                                  {formFields}
                                  <DialogFooter>
                                    <Button type="submit" disabled={updateOrganization.isPending}>
                                      Update Organization
                                    </Button>
                                  </DialogFooter>
                                </form>
                              </DialogContent>
                            </Dialog>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(org)}
                              disabled={deleteOrganization.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data?.organizations.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-muted-foreground">
                          No organizations found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data && data.pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {data.page} of {data.pages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= data.pages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
